import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import logo from "assets/images/logo.png"; 
import Alert from "@mui/material/Alert";
import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import { UpdatePassword } from "../../../axios-client"; 
import CircularProgress from "@mui/material/CircularProgress";
import {Box, Grid, Typography} from "@mui/material";
import LanguageSwitcher from "../../../reusableComponents/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import {setLayout} from "../../../store/actions/app-actions";
import {useSoftUIController} from "../../../context";

function ResetNewPassword() {
    const {token} = useParams();
    const theme = useTheme();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [controller, dispatch] = useSoftUIController();

    const {i18n} = useTranslation();
    const { t } = useTranslation(['resetPassword']);
    const currentLang = i18n.language;
    const signInPage = `/${currentLang}/authentication/sign-in`;
    const features = t('panel.features', { returnObjects: true });

    const [animateTitle, setAnimateTitle] = useState(false);
    const [animateForm, setAnimateForm] = useState(false);
    const [animateImage, setAnimateImage] = useState(false);

    useEffect(() => {
        setTimeout(() => setAnimateImage(true), 300);
        setTimeout(() => setAnimateTitle(true), 500);
        setTimeout(() => setAnimateForm(true), 700);

        setLayout(dispatch, "page");
    }, []);

    
  const handleResetPassword = async (event) => {
    event.preventDefault();

    setErrorMessage(null);
    setSuccessMessage(null);

    if (!newPassword || !confirmPassword) {
        setErrorMessage(t('changePassword.error.both'));
        return;
    }

    if (newPassword !== confirmPassword) {
        setErrorMessage(t('changePassword.error.match'));
        return;
    }

    try {
        await UpdatePassword(token, newPassword, confirmPassword);
        setSuccessMessage(t('changePassword.success'));
        setIsLoading(true);
        setTimeout(() => navigate(signInPage), 3000);
    } catch (error) {
        if (error.response && error.response.data) {
            if (error.response.data.error) {
                setErrorMessage(error.response.data.error);
            } else if (error.response.data.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage("An error occurred, please try again later!");
            }
        } else {
            setErrorMessage("An error occurred, please try again later!");
        }
        setIsLoading(false);
    }
};

  return (
    <>
        <style>
            {`
          @keyframes fadeSlideIn {
            0% { opacity: 0; transform: translateY(20px); }
            100% { opacity: 1; transform: translateY(0); }
          }
         @keyframes scan {
            0% { top: 0;} 
            50% { top: 100%;}
            100% { top: 0;}
            }
        `}
        </style>

        <Grid container sx={{minHeight: "100vh", overflow: "hidden"}}>
            {/* Left Side */}
            <Grid
                item
                xs={12}
                md={6}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    p: 5,
                    backgroundColor: "#ffffff",
                    position: "relative",
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: theme.spacing(2),
                        left: theme.spacing(2),
                        right: theme.spacing(2),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        zIndex: 10,
                    }}
                >
                    <Box
                        sx={{
                            transition: "all 0.8s ease",
                        }}
                    >
                        <img src={logo} alt="YaiGlobal Logo" style={{height: 48}}/>
                    </Box>

                    <LanguageSwitcher/>
                </Box>

                <Box sx={{width: "100%", maxWidth: 400}}>
                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{
                            background: "linear-gradient(to right, #d946ef, #ec4899)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            mb: 1,
                            opacity: animateTitle ? 1 : 0,
                            transform: animateTitle ? "translateX(0)" : "translateX(-10px)",
                            transition: "all 0.7s ease",
                        }}
                    >
                        {t('title')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{
                            mb: 3,
                            opacity: animateTitle ? 1 : 0,
                            transform: animateTitle ? "translateX(0)" : "translateX(-10px)",
                            transition: "all 0.8s ease",
                        }}
                    >
                        {t('description')}
                    </Typography>

                    {errorMessage && (
                        <Alert severity="error" onClose={() => setErrorMessage(null)}>
                            {errorMessage}
                        </Alert>
                    )}

                    {successMessage && (
                        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
                            {successMessage}
                        </Alert>
                    )}

                    <SoftBox component="form" role="form" onSubmit={handleResetPassword}>
                        {isLoading && (
                            <SoftBox display="flex" justifyContent="center" my={2}>
                                <CircularProgress size={24} />
                            </SoftBox>
                        )}
                        <SoftBox mb={2}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                    {t('newPassword')}
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                type="password"
                                placeholder={t('enterNewPassword')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                            />
                        </SoftBox>

                        <SoftBox mb={2}>
                            <SoftBox mb={1} ml={0.5}>
                                <SoftTypography component="label" variant="caption" fontWeight="bold">
                                    {t('confirmNewPassword')}
                                </SoftTypography>
                            </SoftBox>
                            <SoftInput
                                type="password"
                                placeholder={t('confirmNewPassword')}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </SoftBox>

                        <SoftBox mt={4} mb={1}>
                            <SoftButton variant="gradient" color="primary" fullWidth type="submit">
                                {t('resetPassword')}
                            </SoftButton>
                        </SoftBox>
                    </SoftBox>
                </Box>
            </Grid>

            {/* Right Side – Decorative Panel */}
            <Grid item md={6} sx={{display: {xs: "none", md: "block"}, position: "relative", overflow: "hidden"}}>
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        background: "linear-gradient(135deg, rgba(217,70,239,0.9), rgba(126,34,206,0.9))",
                        position: "relative",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: animateImage ? 0.8 : 0,
                        transition: "opacity 1s ease",
                    }}
                >
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 1,
                            p: 4,
                            maxWidth: 500,
                            backgroundColor: "rgba(255,255,255,0.1)",
                            borderRadius: 4,
                            border: "1px solid rgba(255,255,255,0.2)",
                            animation: animateImage ? "fadeSlideIn 1s ease forwards" : "none",
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold" sx={{color: "#fff", mb: 2}}>
                            {t("panel.brand")}
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{color: "#fff"}}>
                            {t("panel.headline")}
                        </Typography>
                        <Typography variant="body2" sx={{color: "rgba(255,255,255,0.8)", mb: 3}}>
                            {t("panel.body")}
                        </Typography>
                        <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                            {features.map((label) => (
                                <Box
                                    key={label}
                                    sx={{
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 50,
                                        backgroundColor: "rgba(255,255,255,0.2)",
                                        fontSize: "0.875rem",
                                        color: "#fff",
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                        </Box>
                        <Box
                            sx={{
                                position: 'absolute',
                                left: 0,
                                width: '100%',
                                height: '1.5px',
                                backgroundColor: 'rgba(250, 204, 21, 0.7)',
                                boxShadow: '0 0 8px 2px rgba(250, 204, 21, 0.5)',
                                animation: 'scan 20s ease-in-out infinite',
                            }}
                        />

                    </Box>
                </Box>

                <Box
                    component="svg"
                    xmlns="http://www.w3.org/2000/svg"
                    sx={{position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.2}}
                >
                    <defs>
                        <pattern id="circuit-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                            <path
                                d="M10,10 L40,10 L40,40 L70,40 L70,70 L40,70 L40,40 M60,10 L90,10 L90,40 M10,30 L10,60 L30,60 M10,80 L40,80 L40,90 L70,90 M60,30 L60,60 L90,60 L90,90"
                                fill="none"
                                stroke="white"
                                strokeWidth="1"
                            />
                        </pattern>
                    </defs>
                    <rect x="0" y="0" width="100%" height="100%" fill="url(#circuit-pattern)"/>
                </Box>
            </Grid>
        </Grid>
    </>
  );
}

export default ResetNewPassword;
